/**
 * Colour tokens.
 *
 * `THEME.x` is a CSS variable reference (`var(--okc-x, <light value>)`), so every
 * style that reads THEME follows the app theme with no per-component work. The
 * variables are declared once, in the document head, by `buildThemeCss()`
 * (server/components/HTML.tsx): light values on `:root`, dark values on
 * `:root[data-theme='dark']`. The light value is also the var() fallback, so
 * anything rendered without that stylesheet (print windows, emails) stays light.
 *
 * Dark values follow the Android app's dark tokens (design/theme/Color.kt):
 * a neutral ramp with no teal tint — 950 #121212 canvas, 900 #1c1c1e surface,
 * 800 #2a2a2d raised, 700 #3a3a3d borders, 300 #b0b0b0 / 50 #f2f2f2 text — one
 * step brighter brand green (#30a74f) and red (#ff5e38), and muted opaque tints
 * instead of the pale light-mode fills.
 *
 * Because values are var() strings, never do colour maths on THEME (no hex +
 * alpha concatenation, no passing to canvas); use LIGHT_THEME / DARK_THEME for a
 * real colour value.
 */
export const LIGHT_THEME = {
  brandDarkerGrey: '#757575',
  brandDarkGrey: '#bdbdbd',
  brandDarkGrey2: '#dae2e6',
  brandDarkGrey3: '#222F2F',
  brandDarGrey4: '#426262',
  brandMediumDarkGrey: '#e5ebee',
  brandMedLightGrey: '#eff3f5',
  brandLightGrey: '#e0e0e0',
  lightGrey: '#f2f2f2',
  darkGrey: '#444444',
  brandMediumGrey: '#9e9e9e',
  brandGrey: '#f0f4f5',
  brandSelectedGrey: '#eff3f5',
  brandLighterGrey: '#f9fafb',
  brandLightestGrey: '#fafafa',
  brandGreyPrimary: '#cbd7dc',
  brandBorderGrey: '#e0dddd',
  brandActivityGrey: '#E7EAEE',
  infoGrey: '#616161',
  brandGreySuperLite: '#4E4E4E',
  brandBGLight: '#f5f5f5',
  brandBGMedium: '#eeeeee',
  brandLinkBlue: '#0000ff',
  brandBlack: '#212121',
  brandBlackAlt: '#424242',
  brandWhite: '#fff',
  brandRed: '#ed4017',
  brandDarkRed: '#d3340f',
  brandMaroon: 'rgba(0, 0, 0, 0.8)',
  brandGreen: '#1c873b',
  brandDarkGreen: '#14742d',
  brandMediumGreen: '#c2e4c8',
  brandLightGreen: '#e6f4e9',
  brandTintGreen: '#117631',
  brandHeartRed: '#E72814',
  brandBlue: '#0b62d1',
  brandAquaBlue: '#0574e3',
  brandMediumBlue: '#c1ddfc',
  brandLightBlue: '#e2f2ff',
  brandOrange: '#ffa558',
  brandLightOrange: '#ffcd00',
  brandLighterRed: '#f1ccc1',
  brandLightestRed: '#fbe5de',
  brandDarkOrange: '#e88c00',
  brandLightRed: '#ffe4dd',
  brandLightRedBorder: '#f8cabf',
  brandTintOrangeBorder: '#ffe3b9',
  brandTintOrange: '#fdefda',
  brandDarkerOrange: '#ff5e38',
  brandWarmOrange: '#fab958',
  brandLightGold: '#fdeeda',
  brandYellow: '#f9aa33',
  lightBlue: '#b9deff',
  brandYellowOrange: '#eead16',
  brandCyanPrimary: '#00abbc',
  brandLightPrimary: '#aaeff5',
  greyBorder: '#e1e1e1',

  // Text / glyphs sitting on a brand-coloured or inverse fill (green buttons, dark chips): white in both modes
  onBrand: '#fff',
  // Dark fill that stays dark in both modes (tooltips, toasts, dark chips) — never use brandBlack as a background
  surfaceInverse: '#212121',
  // App canvas behind cards and panes
  pageBackground: '#f5f5f5',
  // Fill of a disabled control (usually under white text). The mid greys double
  // as TEXT colours, where dark mode must brighten them, so fills get their own
  // token that goes dark instead.
  disabledFill: '#bdbdbd',
  // Android grey50: the pale teal-grey fill of fields, chips and side panels
  grey50: '#f2f8f8',
  // Ink as an RGB triplet, for translucent text / hairlines / hover washes:
  //   color: `rgba(${THEME.inkRgb}, 0.6)`
  inkRgb: '33, 33, 33',
  // App shell: the blue-grey band behind the top bar and the side tabs
  shellBackground: '#cbd7dc',
  shellBorder: '#b4babc',
  // Active side tab disc
  shellActiveTab: '#95bda0',
  // 1-Click Vasooli FAB: a fixed brand CTA, not a themed surface — the same pale orange
  // fill + grey900 ink in both modes (Android orange_200 / grey900, HomeLedgerScreenUi)
  ctaOrangeFill: '#ffe3b9',
  onCtaOrangeFill: '#222f2f',
  // Solid disc behind a list/benefit icon (Android okc.iconDiscMuted: grey300 / darkNeutral700)
  iconDiscMuted: '#afcaca',
  // Downgrade CTA (Android okc.alertStrong = orange_primary #F57C00): fixed in both modes
  ctaOrangeStrong: '#f57c00',
  // Plan-style screens (Android okc.screenBackground / okc.screenCard): white ground with
  // grey50 cards in light; #121212 ground with #1c1c1e cards in dark
  screenBackground: '#fff',
  screenCard: '#f2f8f8',
  // Backdrop behind dialogs and drawers
  scrim: 'rgba(0, 0, 0, 0.32)'
}

export type ThemeToken = keyof typeof LIGHT_THEME

export const DARK_THEME: Record<ThemeToken, string> = {
  brandDarkerGrey: '#b0b0b0',
  brandDarkGrey: '#7a7a7e',
  brandDarkGrey2: '#3a3a3d',
  brandDarkGrey3: '#f2f2f2',
  brandDarGrey4: '#b0b0b0',
  brandMediumDarkGrey: '#3a3a3d',
  brandMedLightGrey: '#2a2a2d',
  brandLightGrey: '#3a3a3d',
  lightGrey: '#2a2a2d',
  darkGrey: '#cfcfcf',
  brandMediumGrey: '#9a9a9e',
  brandGrey: '#2a2a2d',
  brandSelectedGrey: '#2a2a2d',
  brandLighterGrey: '#1f1f22',
  brandLightestGrey: '#1f1f22',
  brandGreyPrimary: '#3a3a3d',
  brandBorderGrey: '#3a3a3d',
  brandActivityGrey: '#2a2a2d',
  infoGrey: '#b0b0b0',
  brandGreySuperLite: '#cfcfcf',
  brandBGLight: '#121212',
  brandBGMedium: '#2a2a2d',
  brandLinkBlue: '#6ea8ff',
  brandBlack: '#f2f2f2',
  brandBlackAlt: '#e6e6e6',
  brandWhite: '#1c1c1e',
  brandRed: '#ff5e38',
  brandDarkRed: '#ff5e38',
  brandMaroon: 'rgba(0, 0, 0, 0.8)',
  brandGreen: '#30a74f',
  brandDarkGreen: '#1c873b',
  brandMediumGreen: '#2e5a3a',
  brandLightGreen: '#203828',
  brandTintGreen: '#30a74f',
  brandHeartRed: '#ff5e38',
  brandBlue: '#5b9df5',
  brandAquaBlue: '#4c9af0',
  brandMediumBlue: '#274766',
  brandLightBlue: '#1b2c3f',
  brandOrange: '#ffa558',
  brandLightOrange: '#ffcd00',
  brandLighterRed: '#5a2e25',
  brandLightestRed: '#46231d',
  brandDarkOrange: '#f9aa33',
  brandLightRed: '#46231d',
  brandLightRedBorder: '#6b3529',
  brandTintOrangeBorder: '#6b5322',
  brandTintOrange: '#3d3118',
  brandDarkerOrange: '#ff5e38',
  brandWarmOrange: '#fab958',
  brandLightGold: '#3d3118',
  brandYellow: '#f9aa33',
  lightBlue: '#274766',
  brandYellowOrange: '#eead16',
  brandCyanPrimary: '#00abbc',
  brandLightPrimary: '#1f4a50',
  greyBorder: '#3a3a3d',

  onBrand: '#fff',
  surfaceInverse: '#3a3a3d',
  pageBackground: '#121212',
  disabledFill: '#3a3a3d',
  grey50: '#2a2a2d',
  inkRgb: '242, 242, 242',
  shellBackground: '#121212',
  shellBorder: '#3a3a3d',
  shellActiveTab: '#203828',
  scrim: 'rgba(0, 0, 0, 0.6)',
  ctaOrangeFill: '#ffe3b9',
  onCtaOrangeFill: '#222f2f',
  iconDiscMuted: '#3a3a3d',
  screenBackground: '#121212',
  screenCard: '#1c1c1e',
  ctaOrangeStrong: '#f57c00'
}

const cssVarName = (token: string) => `--okc-${token}`
// Dark-ONLY twin of each token, used by utils/jssDarkMode for literal colours:
// `var(--okc-d-x, <original literal>)` is the literal in light mode and the
// token's dark value in dark mode.
export const darkVarName = (token: ThemeToken) => `--okc-d-${token}`

export const THEME = (Object.keys(LIGHT_THEME) as ThemeToken[]).reduce((theme, token) => {
  theme[token] = `var(${cssVarName(token)}, ${LIGHT_THEME[token]})`
  return theme
}, {} as Record<ThemeToken, string>)

const declarations = (values: Record<ThemeToken, string>) =>
  (Object.keys(values) as ThemeToken[])
    .map((token) => `${cssVarName(token)}:${values[token]};`)
    .join('')

// JSS nested selector for a dark-only override, for the few styles whose dark
// look is not a plain token swap:  [DARK]: { backgroundColor: ... }
export const DARK = ":root[data-theme='dark'] &"

// Global stylesheet that declares the tokens for both modes. `color-scheme`
// also switches native controls (scrollbars, form fields, date pickers).
//  - `[data-theme-local='light']` pins a subtree to light inside a dark app
//    (things that are captured, shared or printed: QR cards, receipts).
//  - Paper is always light, whatever the screen shows.
const darkOnlyDeclarations = (reset: boolean) =>
  (Object.keys(DARK_THEME) as ThemeToken[])
    .map((token) => `${darkVarName(token)}:${reset ? 'initial' : DARK_THEME[token]};`)
    .join('')

export const buildThemeCss = () => {
  const light = declarations(LIGHT_THEME) + darkOnlyDeclarations(true)
  const dark = declarations(DARK_THEME) + darkOnlyDeclarations(false)
  return (
    `:root{color-scheme:light;${light}}` +
    `:root[data-theme='dark']{color-scheme:dark;${dark}}` +
    `:root[data-theme='dark'] [data-theme-local='light']{color-scheme:light;${light}}` +
    `@media print{:root[data-theme='dark']{color-scheme:light;${light}}}` +
    DARK_FIXES
  )
}

const INK_FILLS = ['#212121', '#222f2f', '#000', '#000000', '#001000', '#010101', 'black']
// Never recolour a QR code (qrcode.react marks its <svg> crispEdges): its dark
// modules on a white quiet zone are what a scanner reads. Same for anything
// pinned to light.
const NOT_QR = ":not([shape-rendering='crispEdges'])"
const NOT_LOCAL_LIGHT = ":not([data-theme-local='light'] *)"
const inkSelector = (attribute: 'fill' | 'stroke') =>
  INK_FILLS.map(
    (value) =>
      `${DARK_ROOT} svg${NOT_QR}[${attribute}='${value}' i]${NOT_LOCAL_LIGHT},` +
      `${DARK_ROOT} svg${NOT_QR} [${attribute}='${value}' i]${NOT_LOCAL_LIGHT}`
  ).join(',')

// Dark-only global fixes for colour that never goes through THEME:
//  - text that simply inherits the browser's default black
//  - inline SVG icons with a baked dark fill (the shared icon library defaults
//    to #212121 when no fill is passed)
//  - SVG files shown through <img>, which CSS cannot reach into: the ones
//    utils/darkImages tags as dark monochrome (`data-dark-invert`) are inverted,
//    so dark glyphs turn light; illustrations, logos and white glyphs are left
//    alone. `data-keep-colors` opts a single <img> out.
const DARK_ROOT = ":root[data-theme='dark']"

// Dialogs and sheets (everything marked role="dialog") sit one step above the
// page in dark — Android's sheetContainer rule. With the page dimmed by the scrim,
// a card in the normal surface colour is the same shade as what is behind it and
// disappears. The surface tokens are re-pointed INSIDE the dialog, so its card,
// fields and inner panels all lift together, and it gets a hairline + deep shadow.
const DIALOG_SURFACES: Partial<Record<ThemeToken, string>> = {
  brandWhite: '#2a2a2d',
  brandLighterGrey: '#2f2f32',
  brandLightestGrey: '#2f2f32',
  brandGrey: '#333336',
  grey50: '#333336',
  lightGrey: '#333336',
  brandBGMedium: '#333336',
  brandMedLightGrey: '#333336',
  brandSelectedGrey: '#333336',
  brandActivityGrey: '#333336',
  brandLightGrey: '#47474a',
  brandGreyPrimary: '#47474a',
  greyBorder: '#47474a',
  brandBorderGrey: '#47474a'
}
const ELEVATED_DIALOGS =
  `${DARK_ROOT} [role='dialog']{` +
  (Object.keys(DIALOG_SURFACES) as ThemeToken[])
    .map(
      (token) =>
        `${cssVarName(token)}:${DIALOG_SURFACES[token]};${darkVarName(token)}:${
          DIALOG_SURFACES[token]
        };`
    )
    .join('') +
  'box-shadow:0 0 0 1px #3a3a3d,0 24px 64px rgba(0,0,0,.6)}'

// Shared-library icons such as DoneCircleIcon sit on a white backing disc (white
// fill AND white stroke) that knocks the glyph out of whatever is behind it. It is
// invisible on a white panel; on a dark one it must take the surface colour or it
// shows as a bright ring.
const KNOCKOUT_BACKING =
  `${DARK_ROOT} svg:not([shape-rendering='crispEdges']) [fill='#fff' i][stroke='#fff' i],` +
  `${DARK_ROOT} svg:not([shape-rendering='crispEdges']) [fill='#ffffff' i][stroke='#ffffff' i]` +
  `{fill:${DARK_THEME.brandWhite};stroke:${DARK_THEME.brandWhite}}`

// A form control with no background of its own shows the browser default: white
// in light, a grey box under color-scheme dark. Make the default see-through
// and inherit the text colour. :where() keeps this at zero specificity, so any
// class that does set a background or colour still wins.
const BARE_FORM_CONTROLS =
  ":where(:root[data-theme='dark']) :where(input:not([type='checkbox']):not([type='radio']):not([type='range']):not([type='color']):not([type='file']), textarea, select)" +
  '{background-color:transparent;color:inherit}'

// Third-party widgets that ship their own light stylesheet.
// @hassanmojab/react-modern-calendar-datepicker:
const CALENDAR = `${DARK_ROOT} .Calendar`
const THIRD_PARTY_DARK =
  `${CALENDAR}{background:${DARK_THEME.brandGrey};box-shadow:0 8px 24px rgba(0,0,0,.5)}` +
  `${CALENDAR}__monthYear,${CALENDAR}__monthText,${CALENDAR}__yearText,` +
  `${CALENDAR}__day:not(.-blank):not(.-selected):not(.-selectedStart):not(.-selectedEnd),` +
  `${CALENDAR}__monthSelectorItemText,${CALENDAR}__yearSelectorText{color:${DARK_THEME.brandBlack}}` +
  `${CALENDAR}__weekDay{color:${DARK_THEME.brandDarkerGrey}}` +
  `${CALENDAR}__day.-disabled,${CALENDAR}__monthSelectorItem.-disabled .Calendar__monthSelectorItemText` +
  `{color:${DARK_THEME.brandDarkGrey} !important}` +
  `${CALENDAR}__day:not(.-blank):not(.-selectedStart):not(.-selectedEnd):not(.-selectedBetween):not(.-selected):hover,` +
  `${CALENDAR}__monthText:hover,${CALENDAR}__yearText:hover,` +
  `${CALENDAR}__monthSelectorItem:not(.-active) .Calendar__monthSelectorItemText:not(:disabled):hover,` +
  `${CALENDAR}__yearSelectorItem:not(.-active) .Calendar__yearSelectorText:not(:disabled):hover` +
  `{background:${DARK_THEME.brandLightGrey}}` +
  `${CALENDAR}__monthSelector,${CALENDAR}__yearSelector,${CALENDAR}__yearSelectorWrapper::after,` +
  `${CALENDAR}__yearSelectorWrapper::before{background:${DARK_THEME.brandGrey}}` +
  `${CALENDAR}__yearSelectorWrapper::after{background-image:linear-gradient(to bottom,${DARK_THEME.brandGrey},${DARK_THEME.brandGrey} 10%,rgba(42,42,45,0))}` +
  `${CALENDAR}__yearSelectorWrapper::before{background-image:linear-gradient(to top,${DARK_THEME.brandGrey},${DARK_THEME.brandGrey} 10%,rgba(42,42,45,0))}` +
  `${CALENDAR}__monthArrow{filter:invert(1)}` +
  `${CALENDAR}__footer{background:${DARK_THEME.brandGrey}}` +
  `${DARK_ROOT} .DatePicker__input{background:${DARK_THEME.brandGrey};color:${DARK_THEME.brandBlack};border-color:${DARK_THEME.brandLightGrey}}` +
  `${DARK_ROOT} .DatePicker__calendarArrow{border-color:transparent transparent ${DARK_THEME.brandGrey} transparent}`
const DARK_FIXES =
  `${DARK_ROOT} body{background:${DARK_THEME.pageBackground};color:${DARK_THEME.brandBlack}}` +
  `${inkSelector('fill')}{fill:${DARK_THEME.brandBlack}}` +
  `${inkSelector('stroke')}{stroke:${DARK_THEME.brandBlack}}` +
  ELEVATED_DIALOGS +
  KNOCKOUT_BACKING +
  BARE_FORM_CONTROLS +
  `${DARK_ROOT} svg[fill='#426262' i],${DARK_ROOT} svg [fill='#426262' i]{fill:${DARK_THEME.brandDarGrey4}}` +
  `${DARK_ROOT} img[data-dark-invert]:not([data-keep-colors])` +
  '{filter:invert(1) hue-rotate(180deg)}' +
  `${DARK_ROOT} [data-theme-local='light'] img{filter:none}` +
  THIRD_PARTY_DARK
